import { IntlProvider, FormattedMessage } from 'react-intl';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Link from '@ux/link';
import CartIcon from '@ux/icon/cart';
import classnames from 'classnames';
import addhoc from 'addhoc';
import { useBreakpoints, isMobile } from '@ux/component-utilities';

const breakpoints = ['mobile', 'phablet', 'tablet', 'desktop', 'large'];
/**
 * Render the checkout link with optional text.
 *
 * @Constructor
 * @class Cart
 * @api public
 */
export class Cart extends Component {

  constructor(props) {
    super(props);

    this.state = {
      items: this.props.items
    };
  }

  /**
   * Render the cart icon, only show the cart if the customer has items in the cart.
   * Only show the icon (no message) if mobile.
   *
   * @returns {ReactElement} Translated cart icon and optional message.
   * @api private
   */
  render() {
    const props = this.props;
    const items =
      typeof this.state.items === 'number' ? this.state.items : props.items;
    const messageId = items
      ? 'Shared:UtilityBar:Cart:ItemsInCartMessage'
      : 'Shared:UtilityBar:Cart:EmptyCartMessage';
    const icon = (
      <span
        aria-label={ props.messages[messageId] }
        className={ classnames('cart-icon', {
          'cart-empty': !items
        }) }>
        <CartIcon width={ 30 } height={ 30 } />
      </span>
    );

    if (!props.showEmpty && !items) {
      return null;
    }

    if (props.iconOnly) {
      return icon;
    }

    // Show just the icon if the cart is empty or if we're in mobile
    if ((props.showEmpty && !items) || isMobile(props.breakpoint) || props.hideCheckoutNow) {
      return (
        <Link
          ariaLabel={ props.messages[messageId] }
          urlArgs={ props.urlArgs }
          href={ props.checkout }
          data-eid={ props.eid }
          className={ props.className }>
          { icon }
        </Link>
      );
    }

    // In non-mobile when the cart has items, show the full "Checkout Now" link
    return (
      <IntlProvider locale={ props.market } messages={ props.messages }>
        <Link
          urlArgs={ props.urlArgs }
          href={ props.checkout }
          data-eid={ props.eid }
          className={ props.className }>
          { icon }
          <span className='checkout-now-text'>
            <FormattedMessage id={ messageId } />
          </span>
        </Link>
      </IntlProvider>
    );
  }
}

/**
 * Helper to update cart items, needs to be bound to proper scope that has state
 * on the number of items before the function can be used.
 *
 * @param {Number} items Updated cart count.
 * @param {Function} done Optional completion callback.
 * @api public
 */
Cart.update = function update(items, done = () => {}) {
  this.setState(
    {
      items: parseInt(items, 10) || 0
    },
    done
  );
};

/**
 * Property definitions.
 *
 * @type {Object}
 * @api public
 */
Cart.defaultProps = {
  eid: 'uxp.hyd.app_header.header.cart.cart_link.link.click',
  market: 'en',
  items: 0,
  showEmpty: false,
  iconOnly: false
};

/**
 * Property definitions.
 *
 * @type {Object}
 * @api private
 */
Cart.propTypes = {
  /** current breakpoint */
  breakpoint: PropTypes.string,

  /** eid to be used link click events */
  eid: PropTypes.string,

  /** Item count representing how many items are in the cart */
  items: PropTypes.number,

  /** If the customer is logged in */
  loggedIn: PropTypes.bool,

  /** Market needed for IntlProvider  */
  market: PropTypes.string,

  /** Checkout url for the cart */
  checkout: PropTypes.string.isRequired,

  /** Messages for IntlProvider */
  messages: PropTypes.object.isRequired,

  /** Show the empty cart icon, default false */
  showEmpty: PropTypes.bool,

  /** Render the icon only, no link, default false */
  iconOnly: PropTypes.bool,

  hideCheckoutNow: PropTypes.bool,
  urlArgs: PropTypes.object,
  className: PropTypes.string
};

const withBreakpoints = addhoc(getWrappedComponent => {
  const { breakpoint } = useBreakpoints(...breakpoints);
  return getWrappedComponent({ breakpoint });
}, 'WithBreakpoints');

export default withBreakpoints(Cart);
